import { useFieldArray, useFormContext } from 'react-hook-form'
import { FormFieldData } from './fields/type'
import { Button } from '@/components/ui/button'
import { Plus, Trash, GripVertical } from 'lucide-react'
import { DetailSubFormHeader } from './DetailSubFormHeader'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { FormFieldRenderer } from './renderer/FormFieldRenderer'
import { useCallback, useMemo, useEffect } from 'react'
import { DetailManyToOneProvider } from './DetailManyToOneProvider'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type Props<T> = {
  name: string
  title: string
  maxLength?: number
  hideAddButton?: boolean
  hideDeleteButton?: boolean
  dnd?: boolean
  getFields: (index: number) => FormFieldData<T>[]
  addButtonText?: string
}

type RegularTableRowProps<T> = {
  index: number
  getRowFields: (index: number) => FormFieldData<T>[]
  hideDeleteButton?: boolean
  onDelete: (index: number) => void
  dnd?: boolean
}

const GeneralTableRow = <T,>({
  index,
  getRowFields,
  hideDeleteButton,
  onDelete,
  dnd,
}: RegularTableRowProps<T>) => {
  return (
    <TableRow>
      {dnd && (
        <TableCell className="w-8 p-0">
          <div className="flex h-full items-center justify-center p-2">
            <GripVertical className="size-4 text-gray-300" />
          </div>
        </TableCell>
      )}
      {getRowFields(index).map((field) => (
        <TableCell key={String(field.key)} className="p-0">
          <FormFieldRenderer renderType="detail" fieldData={field} hideLabel />
        </TableCell>
      ))}
      {!hideDeleteButton && (
        <TableCell>
          <Button
            variant="destructive"
            size="icon"
            onClick={() => onDelete(index)}
            type="button"
          >
            <Trash className="size-4" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  )
}

type SortableTableRowProps<T> = {
  id: string
  index: number
  getRowFields: (index: number) => FormFieldData<T>[]
  hideDeleteButton?: boolean
  onDelete: (index: number) => void
}

const SortableTableRow = <T,>({
  id,
  index,
  getRowFields,
  hideDeleteButton,
  onDelete,
}: SortableTableRowProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'z-10' : ''}
    >
      <TableCell className="w-8 p-0">
        <div
          {...attributes}
          {...listeners}
          className="flex h-full cursor-grab items-center justify-center p-2 hover:bg-gray-100 active:cursor-grabbing"
        >
          <GripVertical className="size-4 text-gray-400" />
        </div>
      </TableCell>
      {getRowFields(index).map((field) => (
        <TableCell key={String(field.key)} className="p-0">
          <FormFieldRenderer renderType="detail" fieldData={field} hideLabel />
        </TableCell>
      ))}
      {!hideDeleteButton && (
        <TableCell>
          <Button
            variant="destructive"
            size="icon"
            onClick={() => onDelete(index)}
            type="button"
          >
            <Trash className="size-4" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  )
}

export const DetailManyToOneTable = <T,>({
  name,
  title,
  maxLength,
  getFields,
  hideAddButton,
  hideDeleteButton,
  dnd,
  addButtonText,
}: Props<T>) => {
  const context = useFormContext()
  const array = useFieldArray({
    name,
    control: context.control,
    keyName: '@id',
  })

  // 드래그 앤 드롭을 위한 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const getRowFields = useCallback(
    (index: number) => {
      const itemName = `${name}[${index}]`
      return getFields(index).map((field) => ({
        ...field,
        key: `${itemName}.${String(field.key)}`,
      }))
    },
    [getFields, name],
  )

  const onDelete = useCallback(
    (index: number) => {
      if (!confirm('정말로 삭제하시겠습니까?')) {
        return
      }

      array.remove(index)

      // for (let i = index; i < array.fields.length; i++) {
      //   array.update(i, {
      //     ...array.fields[i],
      //     order: i + 1,
      //   })
      // }
    },
    [array],
  )

  // 드래그 완료 이벤트 처리
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        const oldIndex = array.fields.findIndex(
          (field) => field['@id'] === active.id,
        )
        const newIndex = array.fields.findIndex(
          (field) => field['@id'] === over.id,
        )

        if (oldIndex !== -1 && newIndex !== -1) {
          // 배열 순서 변경
          array.move(oldIndex, newIndex)

          // order 필드 업데이트 (현재 폼 값을 기반으로)
          setTimeout(() => {
            array.fields.forEach((field, index) => {
              const currentValue = context.getValues(`${name}.${index}`)
              if (currentValue) {
                context.setValue(`${name}.${index}.order`, index + 1)
              }
            })
          }, 0)
        }
      }
    },
    [array, context, name],
  )

  // 드래그 가능한 아이템들의 ID 목록
  const itemIds = useMemo(
    () => array.fields.map((field) => field['@id']),
    [array.fields],
  )

  return (
    <DetailManyToOneProvider name={name} array={array}>
      <div className="space-y-1 px-5">
        <DetailSubFormHeader title={title} />
        <div className="px-2 text-sm text-gray-500">
          총 {array.fields.length}개
        </div>
        <div className="space-y-2">
          {dnd ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="size-8 px-2 py-0"></TableHead>
                    {getFields(0).map((field) => (
                      <TableHead
                        key={String(field.key)}
                        className="h-8 px-2 py-0 text-left text-[13px]"
                      >
                        {field.label}
                      </TableHead>
                    ))}
                    {!hideDeleteButton && (
                      <TableHead className="h-8 px-2 py-0 text-center text-[13px]">
                        삭제
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext
                    items={itemIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {array.fields.map((field, index) => (
                      <SortableTableRow
                        key={field['@id']}
                        id={field['@id']}
                        index={index}
                        getRowFields={getRowFields}
                        hideDeleteButton={hideDeleteButton}
                        onDelete={onDelete}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </DndContext>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {getFields(0).map((field) => (
                    <TableHead
                      key={String(field.key)}
                      className="h-8 px-2 py-0 text-left text-[13px]"
                    >
                      {field.label}
                    </TableHead>
                  ))}
                  {!hideDeleteButton && (
                    <TableHead className="h-8 px-2 py-0 text-center text-[13px]">
                      삭제
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {array.fields.map((field, index) => (
                  <GeneralTableRow
                    key={field['@id']}
                    index={index}
                    getRowFields={getRowFields}
                    hideDeleteButton={hideDeleteButton}
                    onDelete={onDelete}
                    dnd={false}
                  />
                ))}
              </TableBody>
            </Table>
          )}

          {!hideAddButton &&
            (typeof maxLength === 'undefined' ||
              array.fields.length < maxLength) && (
              <Button
                onClick={() =>
                  array.append(
                    getFields(0).reduce(
                      (acc, field) => {
                        const key = String(field.key)
                        if (!(key in acc)) {
                          acc[key] = field.default || null
                        }
                        return acc
                      },
                      {
                        order: array.fields.length + 1,
                      } as any,
                    ),
                  )
                }
                variant="outline"
                size="sm"
                type="button"
              >
                <Plus className="mr-1" size={16} />{' '}
                {addButtonText || `${title} 더 추가하기`}
              </Button>
            )}
        </div>
      </div>
    </DetailManyToOneProvider>
  )
}
